import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Table, Card, Typography, Button, Row, Col, Statistic, message, Tag } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, DollarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../shared/api/AllApi';
import { formatCurrency, toApiNumber } from '../../../utils/formatters';
import { readCachedSystemConfig } from '../../../shared/lib/systemConfig';
import { fixedStart } from '../../../i18';
import { orderDocNo, paymentDocNo } from '../../../shared/lib/documentNumbers';
import DateRangeFilter from '../../../shared/ui/DateRangeFilter';
import useTablePagination from '../../../shared/ui/useTablePagination';
import dayjs from 'dayjs';

const { Title } = Typography;

const CustomerPaymentHistory = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { pagination } = useTablePagination();
  const [payments, setPayments] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [statsPayments, setStatsPayments] = useState([]);
  const [statsTotalPaid, setStatsTotalPaid] = useState(0);
  const [totalOwed, setTotalOwed] = useState(0);
  const cfg = readCachedSystemConfig();
  const mainCode = cfg?.currency_code || 'IQD';
  const secondaryCode = cfg?.secondary_currency_code || '';

  // Helper to parse total_owed string to number
  const parseOwed = (owed) => {
    return toApiNumber(owed, 0);
  };

  const fetchCustomerPayments = useCallback(async () => {
    try {
      const response = await apiClient.get(`/customers/${id}/payments`);
      setPayments(response.data.payments || []);
      setCustomerInfo({
        id: response.data.customer_id,
        name: response.data.customer_name,
        total_owed: response.data.total_owed // Directly use total_owed from API
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payments:', error);
      message.error(t('fetchError', 'Error loading data'));
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchCustomerPayments();
  }, [fetchCustomerPayments]);

  // Defensive: always treat dateRange as array, fallback to [null, null] if null
  const safeDateRange = useMemo(
    () => (Array.isArray(dateRange) ? dateRange : [null, null]),
    [dateRange]
  );

  const filteredPayments = payments.filter(payment => {
    if (!safeDateRange[0] || !safeDateRange[1]) return true;
    const paymentDate = dayjs(payment.created_at);
    return safeDateRange[0] && safeDateRange[1] &&
      paymentDate.isAfter(safeDateRange[0].startOf('day').subtract(1, 'ms')) &&
      paymentDate.isBefore(safeDateRange[1].endOf('day').add(1, 'ms'));
  });

  // Refactored to prevent infinite re-renders
  useEffect(() => {
    setStatsPayments((safeDateRange[0] && safeDateRange[1]) ? filteredPayments : payments);
  }, [safeDateRange, filteredPayments, payments]);

  useEffect(() => {
    setStatsTotalPaid(statsPayments.reduce((sum, p) => sum + toApiNumber(p.amount, 0), 0));
  }, [statsPayments]);

  useEffect(() => {
    setTotalOwed(parseOwed(customerInfo?.total_owed));
  }, [customerInfo]);

  const columns = [
    {
      title: <b>{t('receipt', 'Receipt')}</b>,
      key: 'actions',
      fixed: fixedStart(i18n.language),
      render: (_, record) => record.entry_type === 'payment' ? (
        <Link to={`/customers/${id}/receipts/${record.id}`} target="_blank">
          <Button icon={<PrinterOutlined />} style={{ background: '#faad14', color: '#fff', border: 'none' }} />
        </Link>
      ) : '—',
    },
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => index + 1,
      width: 70,
    },
    {
      title: <b>{t('paymentNo', 'Payment #')}</b>,
      key: 'document_no',
      width: 150,
      render: (_, r) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
          {paymentDocNo(r)}
        </span>
      ),
    },
    {
      title: <b>{t('type', 'Type')}</b>,
      dataIndex: 'entry_type',
      key: 'entry_type',
      render: (value) => <Tag color={value === 'payment' ? 'green' : value?.includes('refund') ? 'red' : 'orange'}>{String(value || 'payment').replaceAll('_', ' ')}</Tag>,
    },
    {
      title: <b>{t('order', 'Order')}</b>,
      key: 'order',
      width: 150,
      render: (_, r) => {
        if (!r.order_id) return '-';
        return (
          <Link to={`/orders/${r.order_id}/view`} style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
            {orderDocNo(r)}
          </Link>
        );
      },
    },
    {
      title: <b>{t('paymentDate', 'Payment Date')}</b>,
      dataIndex: 'created_at',
      key: 'created_at',
      render: function(text) {
        if (!text) return '';
        const date = new Date(text);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
      },
    },
    {
      title: <b>{t('amount', 'Amount')}</b>,
      dataIndex: 'amount',
      key: 'amount',
      render: (text) => formatCurrency(text),
    },
    {
      title: <b>{t('paymentDetails', 'Payment Details')}</b>,
      key: 'currency_breakdown',
      render: (_, record) => {
        const main = toApiNumber(record.amount_main, Number.NaN);
        const sec = toApiNumber(record.amount_secondary, 0);
        if (!Number.isFinite(main) && sec <= 0) return '-';

        const parts = [];
        if (Number.isFinite(main)) {
          parts.push(`${main.toLocaleString()} ${mainCode}`);
        }
        if (sec > 0) {
          parts.push(`${sec.toLocaleString()} ${secondaryCode || 'secondary'}`);
        }
        const rate = record?.exchange_rate?.main_per_secondary;
        const rateText = rate
          ? ` | 1 ${mainCode} = ${Number(rate).toLocaleString()} ${secondaryCode || ''}`.trim()
          : '';
        return `${parts.join(' + ')}${rateText}`;
      },
    },
    {
      title: <b>{t('paymentMethod', 'Payment Method')}</b>,
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (text) => text || '-',
    },
    {
      title: <b>{t('receiver', 'Receiver')}</b>,
      dataIndex: 'receiver',
      key: 'receiver',
      render: (text) => text || '-',
    },
    {
      title: <b>{t('reason', 'Reason')}</b>,
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: <b>{t('sender', 'Sender')}</b>,
      dataIndex: 'sender',
      key: 'sender',
      render: (text) => text || '-',
    },
  ];

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    } else {
      setDateRange([null, null]); // Clear the date range without causing errors
    }
  };

  if (!id) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>{t("invalidCustomerId", "Invalid or missing customer ID")}</h1>
      </div>
    );
  }
  const datePickerValue = safeDateRange.every(date => date === null) ? undefined : safeDateRange;

  return (
    <div style={{ padding: '20px', direction: 'rtl' }}>
      <Card
        style={{
          marginBottom: '20px',
          background: '#f0f2f5',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            {customerInfo ? `${customerInfo.name}` : t('paymentHistory', 'Payment History')}
          </Title>
          <Link to={`/customers`}>
            <Button type="primary" icon={<ArrowLeftOutlined />}>
              {t('back', 'Back')}
            </Button>
          </Link>
        </div>
      </Card>
      <Row gutter={[24, 24]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card style={{ background: '#e6f7ff', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('paymentCount', 'Payment Count')}
              value={statsPayments.length}
              prefix={<DollarOutlined />}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card style={{ background: '#f6ffed', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('totalPaid', 'Total Paid')}
              value={formatCurrency(statsTotalPaid)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card style={{ background: '#fffbe6', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('totalDebt', 'Total Debt')}
              value={formatCurrency(totalOwed)} 
              valueStyle={{ color: totalOwed > 0 ? '#cf1322' : '#3f8600' }}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 'fit-content' }}>
          <DateRangeFilter
            value={datePickerValue}
            onChange={handleDateChange}
            showTime={false}
            showPresets={false}
          />
        </div>
      </div>
      <Card
        style={{
          background: '#ffffff',
          borderRadius: '15px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Table
          dataSource={filteredPayments}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default CustomerPaymentHistory;
